import { Check, Copy } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { cn } from "@lib/cn.ts";
import type { Token } from "@lib/types.ts";

interface Props {
  lines: Token[][];
  /** Plain source, used for the clipboard. */
  code: string;
  /**
   * Literal substitutions applied to both the rendered tokens and the copied
   * text -- how the environment switcher rewrites hostnames without needing a
   * highlighter at runtime.
   */
  replace?: [string, string][];
  showLineNumbers?: boolean;
  filename?: string;
  className?: string;
  maxHeight?: string;
}

function applyAll(text: string, replace: [string, string][]): string {
  return replace.reduce(
    (acc, [from, to]) => (from === to ? acc : acc.split(from).join(to)),
    text,
  );
}

export function CodeBlock({
  lines,
  code,
  replace = [],
  showLineNumbers = false,
  filename,
  className,
  maxHeight,
}: Props) {
  const [copied, setCopied] = useState(false);

  const rendered = useMemo(
    () =>
      replace.length === 0
        ? lines
        : lines.map((line) =>
            line.map((token) => ({
              ...token,
              c: applyAll(token.c, replace),
            })),
          ),
    [lines, replace],
  );

  const plain = useMemo(() => applyAll(code, replace), [code, replace]);

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(plain).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }, [plain]);

  const gutterWidth = `${String(rendered.length).length + 1}ch`;

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-default border border-code-line bg-code-bg",
        className,
      )}
    >
      {filename ? (
        <div className="flex items-center justify-between border-b border-code-line px-3 py-1.5">
          <span className="font-mono text-[11px] tracking-wide text-code-muted">
            {filename}
          </span>
        </div>
      ) : null}

      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy to clipboard"}
        className={cn(
          "absolute right-2 top-2 z-10 rounded-sm2 border border-code-line bg-code-bg-2 p-1.5",
          "text-code-muted opacity-0 transition hover:text-code-text focus-visible:opacity-100",
          "group-hover:opacity-100",
          filename && "top-10",
        )}
      >
        {copied ? (
          <Check className="size-3.5 text-success" aria-hidden />
        ) : (
          <Copy className="size-3.5" aria-hidden />
        )}
      </button>

      <div
        className="gt-scroll-dark overflow-auto"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <pre className="w-fit min-w-full py-3 text-[12.5px] leading-[1.65]">
          <code className="block">
            {rendered.map((line, lineIndex) => (
              // Token arrays are positional and immutable, so the index is the
              // only stable identity available here.
              <span key={lineIndex} className="flex px-3">
                {showLineNumbers ? (
                  <span
                    aria-hidden
                    className="sticky left-0 mr-3 shrink-0 select-none bg-code-bg text-right text-code-muted/60"
                    style={{ width: gutterWidth }}
                  >
                    {lineIndex + 1}
                  </span>
                ) : null}
                <span className="whitespace-pre">
                  {line.length === 0 ? "\n" : null}
                  {line.map((token, tokenIndex) => (
                    <span key={tokenIndex} style={{ color: token.x }}>
                      {token.c}
                    </span>
                  ))}
                </span>
              </span>
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
}
